import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaClock,
  FaMoneyBillWave,
} from "react-icons/fa";
import { servicesData } from "../data/servicesData";
import styles from "./ServiceDetail.module.css";

const ServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const service = servicesData.find((s) => s.id === id);

  if (!service) {
    return (
      <div className={styles.notFound}>
        <h2>Service not found</h2>
        <Link to="/services">Back to Services</Link>
      </div>
    );
  }

  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <>
      <Helmet>
        <title>{service.title} | Muigai Medical Center</title>
        <meta name="description" content={service.fullDesc.slice(0, 160)} />
      </Helmet>

      {/* Hero Section */}
      <section
        className={styles.heroBanner}
        style={{ backgroundImage: `url(${service.bgImage})` }}
      >
        <div className={styles.heroOverlay}></div>
        <div className="container">
          <motion.div
            className={styles.heroContent}
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <button onClick={() => navigate(-1)} className={styles.backButton}>
              <FaArrowLeft /> Back to Services
            </button>
            <motion.div
              className={styles.serviceIcon}
              style={{ color: "#fff" }}
            >
              <service.icon />
            </motion.div>
            <motion.h1 variants={fadeInUp}>{service.title}</motion.h1>
            <motion.p className={styles.heroSubtitle} variants={fadeInUp}>
              {service.shortDesc}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className={styles.contentSection}>
        <div className="container">
          <div className={styles.contentGrid}>
            <div className={styles.mainContent}>
              <h2>Overview</h2>
              <p>{service.fullDesc}</p>

              <h3>What's Included</h3>
              <ul className={styles.featureList}>
                {service.features.map((feature, idx) => (
                  <li key={idx}>
                    <FaCheckCircle className={styles.checkIcon} />
                    {feature}
                  </li>
                ))}
              </ul>

              <h3>Benefits</h3>
              <ul className={styles.benefitList}>
                {service.benefits.map((benefit, idx) => (
                  <li key={idx}>
                    <span className={styles.benefitIcon}>✓</span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            <aside className={styles.sidebar}>
              <div className={styles.infoCard}>
                <h3>Service Information</h3>
                <div className={styles.infoItem}>
                  <FaClock className={styles.infoIcon} />
                  <div>
                    <strong>Duration</strong>
                    <p>{service.duration}</p>
                  </div>
                </div>
                <div className={styles.infoItem}>
                  <FaMoneyBillWave className={styles.infoIcon} />
                  <div>
                    <strong>Price Range</strong>
                    <p>{service.price}</p>
                  </div>
                </div>
                <Link to="/appointment" className={styles.bookButton}>
                  Book Appointment
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className={styles.ctaSection}>
        <div className="container">
          <h2>Ready to schedule your {service.title.toLowerCase()}?</h2>
          <p>
            Our team is here to assist you. Contact us or book online today.
          </p>
          <div className={styles.ctaButtons}>
            <Link to="/appointment" className={styles.ctaPrimary}>
              Book Appointment
            </Link>
            <Link to="/telemedicine" className={styles.ctaSecondary}>
              Consult Online
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServiceDetail;
